import { CellErrorItem } from '~/app/pages/SurgeonScheduler/TablesLayout/shared/CellErrorHoverCard'
import { day } from '~/astra'
import { useStore } from '~/store/store'
import {
    Department,
    Location,
    LocationSchedule,
    Practitioner,
    PractitionerCellLocation,
    PractitionerSchedule,
    selectGetOpeningHours,
    usePractitioner,
    usePractitionerScheduleLocations,
    usePractitionerScheduleStatuses,
} from '~/storm'

export function isPractitionerAndLocationSameDepartment(practitionerDepartments: Department[], locationDepartments: Department[]) {
    const locationDepartmentIds = locationDepartments.map(department => department.id)
    const practitionerDepartmentIds = practitionerDepartments.map(department => department.id)

    return practitionerDepartmentIds.some(department => locationDepartmentIds.includes(department))
}

export type PractitionerCell = Pick<PractitionerSchedule, 'practitioner_id' | 'scheduled_date'>

export type PractitionerCellErrors = {
    errorsByLocation: Map<string, CellErrorItem[]>
    cell: PractitionerCell
    practitioner: Practitioner | undefined
    hasErrors: boolean
}
export const usePractitionerCellErrors = (cell: PractitionerCell, cellLocations: PractitionerCellLocation[]): PractitionerCellErrors => {
    const { practitioner_id, scheduled_date } = cell
    const dayDate = day(scheduled_date)
    const practitioner = usePractitioner({ where: { id: practitioner_id }, include: { departments: true } })
    const getOpeningHours = useStore(selectGetOpeningHours).byLocationDay

    const errorsByLocation = new Map<string, CellErrorItem[]>()

    cellLocations.forEach(location => {
        const errorList: CellErrorItem[] = []

        if (getOpeningHours(location, dayDate).isClosed) {
            errorList.push({
                label: 'Stue stengt',
                description: 'Stuen bør fjernes',
                sources: [location.room_code],
            })
        }
        if (practitioner?.departments && !isPractitionerAndLocationSameDepartment(practitioner.departments, location.departments)) {
            errorList.push({
                label: 'Tildelt annen avdeling',
                sources: [location.room_code],
            })
        }
        if (errorList.length > 0) {
            errorsByLocation.set(location.room_code, errorList)
        }
    })
    return { errorsByLocation, cell, practitioner: practitioner, hasErrors: errorsByLocation.size > 0 }
}

export type LocationCell = Pick<LocationSchedule, 'scheduled_date'> & {
    location: Location
}

export type LocationCellErrors = {
    errorsByPractitioner: Map<number, CellErrorItem[]>
    cell: LocationCell
    location: Location
    hasErrors: boolean
}

export const useLocationCellErrors = (cell: LocationCell): LocationCellErrors => {
    const { location, scheduled_date } = cell
    const location_id = location.id

    const practitionerScheduleLocationsForCell = usePractitionerScheduleLocations({
        where: { location_id, scheduled_date },
    })
    const practitionerIds = practitionerScheduleLocationsForCell.map(schedule => schedule.practitioner_id)
    const practitionerIdFilter = { practitioner_id: { in: practitionerIds } }

    const allLocationsByPractitioner = usePractitionerScheduleLocations({
        where: { scheduled_date: scheduled_date, ...practitionerIdFilter },
        include: { location: true },
        groupBy: 'practitioner_id',
    })
    const operatingStatusesByPractitioner = usePractitionerScheduleStatuses({
        where: {
            scheduled_date: scheduled_date,
            ...practitionerIdFilter,
            OR: [{ definition: { assignment: 'surgery' } }, { definition: { assignment: 'on_call_surgery' } }],
        },
        include: { definition: true },
        groupBy: 'practitioner_id',
    })

    const getOpeningHours = useStore(selectGetOpeningHours).byLocationDay
    const isClosed = getOpeningHours(location, day(scheduled_date)).isClosed

    const errorsByPractitioner = new Map<number, CellErrorItem[]>()
    practitionerIds.forEach(practitionerId => {
        const errorList: CellErrorItem[] = []
        if (isClosed) {
            errorList.push({
                label: 'Stue stengt',
                description: 'Kirurgen bør fjernes',
            })
        }

        const practitionerLocations = allLocationsByPractitioner.get(practitionerId) ?? []
        if (practitionerLocations.length > 1) {
            errorList.push({
                label: 'Dobbelbooket',
                description: 'Kirurgen er tildelt flere stuer samme dag',
                sources: practitionerLocations.map(schedule => schedule.location?.room_code).filter(Boolean),
            })
        }

        const isOperating = (operatingStatusesByPractitioner.get(practitionerId)?.length ?? 0) > 0
        if (!isOperating) {
            errorList.push({
                label: 'Opererer ikke',
                description: 'Kirurgen bør tildeles operasjonsaktivitet',
            })
        }

        if (errorList.length > 0) {
            errorsByPractitioner.set(practitionerId, errorList)
        }
    })

    return { errorsByPractitioner, cell, location, hasErrors: errorsByPractitioner.size > 0 }
}
